import { nextStateHistory, StateHistory } from 'aurelia-store';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import isEqualWith from 'lodash-es/isEqualWith';
import uniqWith from 'lodash-es/uniqWith';

import { AppState, initialState } from '~/app-store';
import {
	NavigationBookmark,
	NavigationHistory,
	NavigationViewStructure,
} from '~/components/navigation/navigation-types';
import { isBookmarkEqual } from '~/components/navigation/navigation-utils';
import { NAVIGATION_HISTORY_LIMIT } from '~/constants';

const CollapseNavigation = (
	stateHistory: StateHistory<AppState>,
	collapsed: boolean = initialState.navigation.collapsed,
) => {
	const newState: AppState = cloneDeep(stateHistory.present);
	newState.navigation.collapsed = collapsed;
	return nextStateHistory<AppState>(stateHistory, newState);
};

const ToggleBookmark = (
	stateHistory: StateHistory<AppState>,
	bookmarkItem: NavigationBookmark,
) => {
	const newState: AppState = cloneDeep(stateHistory.present);

	// Find existing bookmark
	const bookmarkIndex = stateHistory.present.navigation.bookmarks.findIndex(
		(bookmark) => isEqualWith(bookmark, bookmarkItem, isBookmarkEqual),
	);
	if (bookmarkIndex === -1) {
		// If it doesn't exist yet, add it
		newState.navigation.bookmarks.push(bookmarkItem);
	} else {
		// Else, remove it
		newState.navigation.bookmarks.splice(bookmarkIndex, 1);
	}

	// Keep unique
	newState.navigation.bookmarks = uniqWith(
		newState.navigation.bookmarks,
		isEqual,
	);

	return nextStateHistory<AppState>(stateHistory, newState);
};

const LogNavigationHistory = (
	stateHistory: StateHistory<AppState>,
	newHistory: NavigationHistory,
) => {
	// Check last added history item, if it's equal to the item we're trying to add,
	// do not add to the list.
	const navigationHistory = stateHistory.present.navigation.history;
	if (navigationHistory.length > 0) {
		const lastHistory = navigationHistory[0];
		if (isBookmarkEqual(newHistory, lastHistory)) {
			return stateHistory;
		}
	}

	const newState: AppState = cloneDeep(stateHistory.present);

	// Add new history to head
	newState.navigation.history.unshift(newHistory);

	// Keep unique and trim extra ones
	newState.navigation.history = uniqWith(
		newState.navigation.history,
		isEqual,
	);
	newState.navigation.history.splice(NAVIGATION_HISTORY_LIMIT);

	return nextStateHistory<AppState>(stateHistory, newState);
};

export const NavigationActions = {
	CollapseNavigation,
	LogNavigationHistory,
	ToggleBookmark,
};
